class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    
    this.isUpdatingProtection = false;
    this.isUpdatingMultiplier = false; // New flag to prevent double-clicks

    this.observer = new MutationObserver(() => {
        this._reinit();
    });
  }

  connectedCallback() {
    this.setHeaderCartIconAccessibility();
    this._reinit();
    
    const config = { childList: true, subtree: true };
    const target = this.querySelector('#CartDrawer') || this;
    this.observer.observe(target, config);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event && event.source === 'product-form') {
           this.open();
        }
      });
    }
  }

  _reinit() {
    this.initAOVCarousel();
    this.initUpsellAdd();
    this.initShippingProtection(); 
    this.initMultipliers(); // Add this line here
    
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }
  }

  // --- New Multiplier Logic Starts Here ---
  initMultipliers() {
    const multiplierButtons = this.querySelectorAll('.m-tick');
    if (!multiplierButtons.length) return;

    multiplierButtons.forEach(button => {
      if (button.hasAttribute('data-bound')) return;
      button.setAttribute('data-bound', 'true');

      button.addEventListener('click', (e) => {
        e.preventDefault();
        if (this.isUpdatingMultiplier || button.classList.contains('is-active')) return;
        
        const variantId = button.getAttribute('data-variant-id');
        this.updateMultiplier(variantId);
      });
    });
  }

 updateMultiplier(variantId) {
  if (this.isUpdatingMultiplier) return;
  this.isUpdatingMultiplier = true;

  const container = this.querySelector('.multiplier-upsell-container');
  if (container) container.style.opacity = '0.5';

  // 1. Identify if the clicked button is already active (to remove it)
  const clickedButton = this.querySelector(`.m-tick[data-variant-id="${variantId}"]`);
  const isRemoving = clickedButton && clickedButton.classList.contains('is-active');

  // 2. Collect all current multiplier IDs to ensure we clear any existing ones
  const allMultiplierIds = Array.from(this.querySelectorAll('.m-tick'))
    .map(btn => btn.getAttribute('data-variant-id'));

  const sections = this.getSectionsToRender().map((section) => section.id);

  // 3. Prepare the update: Set all potential multiplier quantities to 0
  let updates = {};
  allMultiplierIds.forEach(id => {
    updates[id] = 0;
  });

  // 4. If we aren't just removing, set the new variant to 1
  if (!isRemoving) {
    updates[variantId] = 1;
  }

  // Use /cart/update.js to handle multiple changes (removals + add) in one request
  fetch(`${routes.cart_update_url}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/javascript',
      'X-Requested-With': 'XMLHttpRequest'
    },
    body: JSON.stringify({
      updates: updates,
      sections: sections,
      sections_url: window.location.pathname
    })
  })
  .then((response) => response.json())
  .then((parsedState) => {
    this.renderContents(parsedState, true);
  })
  .catch((e) => console.error(e))
  .finally(() => {
    this.isUpdatingMultiplier = false;
    if (container) container.style.opacity = '1';
  });
}
  // --- New Multiplier Logic Ends Here ---

  initShippingProtection() {
    const protectionWrapper = this.querySelector('.shipping-protection-wrapper');
    const protectionContainer = this.querySelector('.protection-container');
    const protectionToggle = this.querySelector('#shipping-protection-toggle');
    
    if (!protectionWrapper || !protectionContainer || !protectionToggle) return;

    const variantId = protectionWrapper.getAttribute('data-variant-id');
    const isCurrentlyInCart = protectionContainer.classList.contains('is-active');
    const hasRealItems = Array.from(this.querySelectorAll('.cart-item')).filter(item => item.style.display !== 'none').length > 0;

    if (isCurrentlyInCart && !hasRealItems && !this.isUpdatingProtection) {
      this.updateProtection(variantId, 'remove');
      return;
    }

    if (hasRealItems && !isCurrentlyInCart && !this.isUpdatingProtection) {
        this.updateProtection(variantId, 'add');
        return;
    }

    if (!protectionContainer.hasAttribute('data-bound')) {
      protectionContainer.setAttribute('data-bound', 'true');
      protectionContainer.style.cursor = 'pointer';

      protectionContainer.addEventListener('click', (e) => {
        if (e.target.tagName === 'INPUT') return;
        const action = protectionContainer.classList.contains('is-active') ? 'remove' : 'add';
        this.updateProtection(variantId, action);
      });

      protectionToggle.addEventListener('change', (e) => {
        const action = e.target.checked ? 'add' : 'remove';
        this.updateProtection(variantId, action);
      });
    }
  }

  updateProtection(variantId, action) {
    if (this.isUpdatingProtection) return;
    this.isUpdatingProtection = true;

    const container = this.querySelector('.protection-container');
    if (container) container.style.opacity = '0.5';

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    
    let url = (action === 'remove') ? `${routes.cart_change_url}` : `${routes.cart_add_url}`;
    let body = JSON.stringify({
      id: variantId,
      quantity: (action === 'remove' ? 0 : 1),
      sections: this.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname
    });

    config.body = body;

    fetch(url, config)
      .then((response) => response.json())
      .then((parsedState) => {
        this.renderContents(parsedState, true);
      })
      .catch((e) => console.error(e))
      .finally(() => {
        this.isUpdatingProtection = false;
      });
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');
    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }
    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });
  }

  initUpsellAdd() {
    const upsellButtons = this.querySelectorAll('.AOV-CartDrawer-Upsell__AddBtn');
    upsellButtons.forEach(button => {
      if (button.hasAttribute('data-bound')) return;
      button.setAttribute('data-bound', 'true');
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const itemContainer = button.closest('.AOV-CartDrawer-Upsell__Item');
        const variantSelect = itemContainer.querySelector('.AOV-Upsell-Select');
        const variantId = variantSelect ? variantSelect.value : button.getAttribute('data-variant-id');
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('sections', this.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        config.body = formData;
        button.classList.add('loading');
        button.innerText = '...';
        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((parsedState) => { this.renderContents(parsedState, true); })
          .catch((e) => console.error(e));
      });
    });
  }

  initAOVCarousel() {
    const carouselEl = this.querySelector('#CartDrawerCarousel');
    const container = this.querySelector('.AOV-Carousel__container');
    const slides = this.querySelectorAll('.AOV-Carousel__slide');
    if (!carouselEl || !container || slides.length <= 1 || carouselEl.hasAttribute('data-bound')) return;
    carouselEl.setAttribute('data-bound', 'true');
    if (this.aovInterval) clearInterval(this.aovInterval);
    const duration = parseInt(carouselEl.getAttribute('data-duration')) || 3000;
    let index = 0;
    const updateSlider = () => { container.style.transform = `translateX(-${index * 100}%)`; };
    this.querySelector('.AOV-Carousel__control--next')?.addEventListener('click', (e) => {
        e.preventDefault(); index = (index + 1) % slides.length; updateSlider();
    });
    this.querySelector('.AOV-Carousel__control--prev')?.addEventListener('click', (e) => {
        e.preventDefault(); index = (index - 1 + slides.length) % slides.length; updateSlider();
    });
    this.aovInterval = setInterval(() => { index = (index + 1) % slides.length; updateSlider(); }, duration);
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.classList.add('animate', 'active');
    document.body.classList.add('overflow-hidden');
    this.addEventListener('transitionend', () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      }, { once: true }
    );
  }

  close() {
    this.classList.remove('active');
    if (this.aovInterval) {
        clearInterval(this.aovInterval);
        this.querySelector('#CartDrawerCarousel')?.removeAttribute('data-bound');
    }
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  renderContents(parsedState, forceOpen = false) {
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this._reinit();

    if (forceOpen || this.classList.contains('active')) {
        setTimeout(() => this.open(), 10);
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' }
    ];
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (e) => { e.preventDefault(); this.open(cartLink); });
  }

  setActiveElement(element) { this.activeElement = element; }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);